import * as React from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';

import { cn } from '../lib/cn';
import { PromoCounter } from '../components/PromoCounter';

export const Route = createFileRoute('/start')({
  component: SimpleSignup,
});

interface SignupForm {
  clinic_name: string;
  contact_email: string;
  contact_phone: string;
  vertical: 'vet' | 'medical';
}

interface SignupResponse {
  id: string;
  slug: string;
  preview_url: string;
}

const API = import.meta.env.VITE_API_BASE_URL ?? '';

async function signup(form: SignupForm): Promise<SignupResponse> {
  // Re-using the legacy /onboarding/* endpoints until a dedicated
  // /api/public/signup ships. The endpoint needs `legal_name` in the
  // first POST to seed a unique slug, so we send it there + repeat the
  // full basics slice in the PATCH.
  const startRes = await fetch(`${API}/api/public/onboarding/start`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ legal_name: form.clinic_name }),
  });
  if (!startRes.ok) {
    const text = await startRes.text().catch(() => '');
    throw new Error(`signup start failed (${startRes.status}): ${text}`);
  }
  const { draft_id, slug } = (await startRes.json()) as { draft_id: string; slug: string };

  const patchRes = await fetch(`${API}/api/public/onboarding/${draft_id}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      slice: 'basics',
      basics: {
        legal_name: form.clinic_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        address: { street: '', city: '', zip: '' },
        vertical: form.vertical,
      },
    }),
  });
  if (!patchRes.ok) {
    const text = await patchRes.text().catch(() => '');
    throw new Error(`signup patch failed (${patchRes.status}): ${text}`);
  }

  const submitRes = await fetch(`${API}/api/public/onboarding/${draft_id}/submit`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ tier: 'pozyskaj', preset: 'micropage' }),
  });
  if (!submitRes.ok) {
    const text = await submitRes.text().catch(() => '');
    throw new Error(`signup submit failed (${submitRes.status}): ${text}`);
  }
  return { id: draft_id, slug, preview_url: `https://${slug}.pozyskajpacjenta.pl` };
}

function SimpleSignup() {
  const navigate = useNavigate();
  const [form, setForm] = React.useState<SignupForm>({
    clinic_name: '',
    contact_email: '',
    contact_phone: '',
    vertical: 'vet',
  });

  const m = useMutation({
    mutationFn: signup,
    onSuccess: (r) => navigate({ to: '/dziekujemy', search: { draft: r.id } }),
  });

  function set<K extends keyof SignupForm>(k: K, v: SignupForm[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const canSubmit =
    form.clinic_name.trim().length >= 2 &&
    /^\S+@\S+\.\S+$/.test(form.contact_email) &&
    form.contact_phone.replace(/\D/g, '').length >= 7;

  return (
    <section className="px-6 py-12 md:py-16">
      <div className="mx-auto max-w-xl">
        {/* Live promo counter — shows N/100 + countdown to 2026-07-01 */}
        <div className="mb-6">
          <PromoCounter variant="banner" />
        </div>

        <h1 className="text-center font-display text-3xl font-semibold tracking-tight text-text md:text-4xl">
          Zajmij pakiet założycielski
        </h1>
        <p className="mt-3 text-center text-text-muted">
          3 pola, jedna minuta. Płacisz 99 zł raz — dostajesz tyle miesięcy
          działania, w którym pakiecie się rejestrujesz.
        </p>

        <form
          className="mt-8 space-y-5 rounded-2xl bg-bg p-6 ring-1 ring-border md:p-8"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit && !m.isPending) m.mutate(form);
          }}
        >
          {/* Vertical toggle */}
          <div>
            <label className="text-xs font-medium uppercase tracking-wide text-text-subtle">
              Specjalizacja
            </label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {(['vet', 'medical'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set('vertical', v)}
                  className={cn(
                    'rounded-lg px-4 py-2.5 text-sm font-medium ring-1 transition',
                    form.vertical === v
                      ? 'bg-accent text-bg ring-accent'
                      : 'bg-surface text-text ring-border hover:ring-text/20',
                  )}
                >
                  {v === 'vet' ? 'Weterynaria' : 'Medycyna / stomatologia'}
                </button>
              ))}
            </div>
          </div>

          <Field
            label="Nazwa kliniki"
            required
            placeholder="np. Klinika Weterynaryjna Animal Active"
            value={form.clinic_name}
            onChange={(v) => set('clinic_name', v)}
          />
          <Field
            label="E-mail kontaktowy"
            required
            type="email"
            placeholder="kontakt@klinika.pl"
            value={form.contact_email}
            onChange={(v) => set('contact_email', v)}
            hint="Wyślemy tu link do panelu, jak strona będzie gotowa."
          />
          <Field
            label="Telefon"
            required
            type="tel"
            placeholder="+48 22 123 45 67"
            value={form.contact_phone}
            onChange={(v) => set('contact_phone', v)}
            hint="Dla powiadomień SMS o nowych rezerwacjach."
          />

          <button
            type="submit"
            disabled={!canSubmit || m.isPending}
            className={cn(
              'mt-2 inline-flex w-full items-center justify-center rounded-full px-6 py-3 text-base font-medium transition',
              canSubmit && !m.isPending
                ? 'bg-text text-bg hover:bg-text/90'
                : 'bg-text/40 text-bg cursor-not-allowed',
            )}
          >
            {m.isPending ? 'Tworzymy konto…' : 'Zajmij darmowe miejsce →'}
          </button>

          {m.isError && (
            <p className="text-center text-sm text-red-600">
              Coś poszło nie tak. Spróbuj ponownie lub napisz na hej@pozyskajpacjenta.pl.
            </p>
          )}

          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-text-subtle">
            <ShieldCheck className="size-3.5" aria-hidden="true" />
            Bez karty kredytowej. Bez zobowiązań. Możesz w każdej chwili usunąć konto.
          </p>
        </form>

        {/* What's included */}
        <div className="mt-8 rounded-xl bg-surface/60 p-5 ring-1 ring-border">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-text-subtle">
            Co dostajesz
          </h2>
          <ul className="mt-3 grid gap-2 text-sm text-text-muted md:grid-cols-2">
            {STARTER_FEATURES.map((f) => (
              <li key={f} className="flex gap-2">
                <span className="text-accent">✓</span> <span>{f}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-text-subtle">
            Po 1 lipca lub po wyczerpaniu 100 miejsc: 99 zł/mies, jeden plan.
            Bez prowizji od wizyt. <Link to="/cennik" className="text-accent hover:underline">Zobacz cennik</Link>.
          </p>
        </div>
      </div>
    </section>
  );
}

const STARTER_FEATURES = [
  'Jednostronicowa strona na subdomenie pozyskajpacjenta.pl',
  'Formularz kontaktowy → e-maile lecą do Ciebie',
  'Kalendarz rezerwacji online',
  'Możliwość osadzenia ZnanyLekarz lub Booksy',
  'Hosting + SSL + CDN przez Cloudflare',
  'Treści SEO generowane automatycznie',
];

function Field({
  label,
  required,
  type = 'text',
  placeholder,
  value,
  onChange,
  hint,
}: {
  label: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-text">
        {label}
        {required && <span className="text-accent"> *</span>}
      </span>
      <input
        type={type}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 block w-full rounded-lg bg-surface px-3.5 py-2.5 text-sm text-text ring-1 ring-border placeholder:text-text-subtle focus:outline-none focus:ring-2 focus:ring-accent"
      />
      {hint && <span className="mt-1 block text-xs text-text-subtle">{hint}</span>}
    </label>
  );
}
